import { useEffect, useMemo, useState } from "react";
import { APIS } from "./api";
import { pushEventToGTM } from "../../commonHelpers/pushEventToGTM";
import { sendErrorToSentry } from "ErrorBoundary/sendErrorToSentry";
import { interactedLoginViaOtpByClassroomLinkedParentEvent, interactedPtlParentLoginScreenEvent, ptlParentLoginAttemptedEvent, ptlParentLoginFailedEvent, viewedLoginViaOtpByClassroomLinkedParentEvent, viewedPtlParentLoginScreenEvent } from "../../ptlSignup/events";
import { InvitationSources, REFERRER_PAGE_MAP } from "../../teacherDashboard/utils/constants";
import { IReferralData } from "../../ptlSignup/MergeStudent";
import { DateDiffInDays } from "../../teacherDashboard/teacher/config/common";

let resendOTPTimer: any = null;

const getParams = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token");

    try {
        return token ? JSON.parse(atob(token)) : {};
    } catch (e) {
        sendErrorToSentry(e, {token});
    }
    return {};
}

export const useLogin = ({
    primaryInputLabel = "Email",
    showSocialLogin = true,
    sendEvent = (clickedOn: string) => { },
    source = null,
    isPtlFlow = false
}: any) => {
    const params = getParams();

    const [otpLogin, setOTPLogin] = useState(false);
    const [otpSent, setOTPSent] = useState(false);
    const [otp, setOTP] = useState("");
    const [timer, setTimer] = useState(0);
    const [email, setEmail] = useState(params?.masked_email);
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [intentId, setIntentId] = useState(null);
    const [showResendSuccessMsg, setShowResendSuccessMsg] = useState(false);
    const ifEmailExistInLS = localStorage.getItem('email-entered');
    const [fromPage, setFromPage] = useState((params?.referral_type === InvitationSources.copy_link) ? "Student selection screen" : "Sign up link");
    const [emailEvents, setEmailEvents] = useState({ typed: false, pasted: false });
    const [passwordEvents, setPasswordEvents] = useState({ typed: false, pasted: false });
    const [referralData, setReferralData] = useState<IReferralData>({ referrerPage: "", invitationSource: "" });

    useEffect(() => {
        if (ifEmailExistInLS) {
            setEmail(ifEmailExistInLS);
            localStorage.removeItem('email-entered');
        }
        const referrerPage = REFERRER_PAGE_MAP[params?.referrer_code] || "default";
        const invitationSource = params?.referral_type || "";
        setReferralData({ referrerPage, invitationSource });
        return () => {
            if (resendOTPTimer) {
                resendOTPTimer = null;
                clearInterval(resendOTPTimer);
            }
        };
    }, []);

    useEffect(() => {
        if(isPtlFlow){
            if(otpLogin){
                viewedLoginViaOtpByClassroomLinkedParentEvent({fromPage: fromPage, referrerPage: 
                    referralData?.referrerPage || REFERRER_PAGE_MAP[params?.referrer_code] || "default", invitationSource: referralData?.invitationSource || params?.referral_type });
            }
            else {
                const assignmentDueInDays = params?.nearest_due_date && (params?.nearest_due_date !== "null") ? String(DateDiffInDays(new Date(), new Date(params?.nearest_due_date))) : "na";
                const viewedDueDateOnInvitation = params.view_due_date === "true" ? true : false;
                const totalAssignmentCount = params?.task_count && (params?.task_count !== "null") ? Number(params.task_count || 0): 0;
                viewedPtlParentLoginScreenEvent({ fromPage: fromPage, ptlFlow: params?.user_id ? "PTL student login": "PTL student linking", referrerPage: referralData?.referrerPage || REFERRER_PAGE_MAP[params?.referrer_code] || "default", invitationSource: referralData?.invitationSource || params?.referral_type, assignmentDueInDays, viewedDueDateOnInvitation, totalAssignmentCount });
            }
        }
    },[otpLogin])

    const setOTPTimer = (timeInSeconds: number = 59) => {
        if (!resendOTPTimer) {
            setTimer(timeInSeconds);
            resendOTPTimer = setInterval(() => {
                setTimer((count) => {
                    if (count == 1) {
                        clearInterval(resendOTPTimer);
                        resendOTPTimer = null;
                    }
                    return count - 1;
                });
            }, 1000);
        }
    };

    const sendOTP = () => {
        if(isPtlFlow && intentId){
            interactedLoginViaOtpByClassroomLinkedParentEvent({ clickedOn: "Resend code", interactionDetails: "", otpEntered: (otp?.length === 6), ...referralData });
        }
        if (email?.length) {
            setError(null);
            APIS[intentId ? "resendOTP" : "sendOTP"](email, intentId, params?.user_id).then((res: any) => {
                if (resendOTPTimer) {
                    clearInterval(resendOTPTimer);
                    resendOTPTimer = null;
                }
                setOTP("");
                setOTPTimer();
                setOTPSent(true);
                if (intentId) {
                    setShowResendSuccessMsg(true);
                    setTimeout(() => {
                        setShowResendSuccessMsg(false);
                    }, 5000);
                }
                if (res?.data?.id) {
                    setIntentId(res?.data?.id);
                }
            })
                .catch((err) => {
                    setError(
                        err?.response?.data?.debug_message ||
                        err?.response?.data?.error ||
                        "something went wrong! try again"
                    );
                });
            sendEvent(intentId ? `Resend Verification Code` : `Send Verification Code`);
        } else {
            setError("Can't be blank");
        }
    };

    const handleLogin = (event: any) => {
        const new_ptl_flow = window.location.pathname.includes("/ptl-login");
        event && event.stopPropagation();
        event && event.preventDefault();
        const userType = document.querySelector('.tab.active')?.textContent?.trim() ?? '';
        const emailRegex = /^[a-zA-Z0-9,!#\$%&'\*\+\/=\?\^_`\{\|}~-]+(\.[a-zA-Z0-9,!#\$%&'\*\+\/=\?\^_`\{\|}~-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.([a-zA-Z]{2,})$/;
        if (!email?.length) {
            setError("Can't be blank");
            return;
        }
        if (!emailRegex?.test(email)) {
            setError("Invalid Email");
            return;
        }
        if(isPtlFlow){
            if(otpLogin){
                interactedLoginViaOtpByClassroomLinkedParentEvent({ clickedOn: "Submit", interactionDetails: "", otpEntered: (otp?.length === 6), ...referralData });
            }
            else {
                interactedPtlParentLoginScreenEvent({ clickedOn: "Log in", interactionDetails: "", ...referralData });
            }
        }

        if (otpLogin) {
            if (otpSent) {
                if (!otp?.length) {
                    setError("Can't be blank");
                    return;
                }
                if (otp?.length === 6 && intentId) {
                    if(isPtlFlow){
                        ptlParentLoginAttemptedEvent({ isEmailUpdated: params?.masked_email !== email, loginMethod: otpLogin ? "Verification code" : "Password", ...referralData });
                    }
                    let user_type = new_ptl_flow ? "ptl_parent" : "";
                    APIS.verifyOTP(intentId, email, otp, params.user_id, user_type, params.student_id, params.referral_type, params.referrer_code, new_ptl_flow, params?.trd_id)
                        .then((res: any) => {
                            if (res?.data?.url) {
                                //@ts-ignore
                                // showFlashMessage(`You have successfully logged in`, "success");
                                pushEventToGTM('logged_in', { user_type: userType, label: 'Otp Login' })
                                window.location.href = res?.data?.url || "/";
                            }
                        })
                        .catch((err) => {
                            pushEventToGTM('login_failed', { user_type: userType, label: 'Otp Login' })
                            setError(
                                err?.response?.data?.debug_message ||
                                err?.response?.data?.error ||
                                "something went wrong! try again"
                            );
                        });
                } else {
                    setError("Incorrect verification code. Please double-check the code and try again");
                }
                sendEvent(`Submit Login Details : Verification Code`);
            } else {
                sendOTP();
            }
        } else {
            if (email && password) {
                if(isPtlFlow){
                    ptlParentLoginAttemptedEvent({ isEmailUpdated: params?.masked_email !== email, loginMethod: otpLogin ? "Verification code" : "Password", ...referralData });
                }
                APIS.commonPasswordLogin(params.user_id ? params.user_id : email, password, source, params.student_id, params.referral_type, params.referrer_code, new_ptl_flow, params?.trd_id)
                    .then((res) => {
                        if (res?.data?.success) {
                            if (res?.data?.goto) {
                                //@ts-ignore
                                // showFlashMessage(`You have successfully logged in`, "success");
                                pushEventToGTM('logged_in', { user_type: userType, label: 'Email' });
                                window.location.href = res?.data?.goto;
                            }
                        }
                    })
                    .catch((err) => {
                        pushEventToGTM('login_failed', { user_type: userType, label: 'Email' })
                        setError(
                            err?.response?.data?.debug_message ||
                            err?.response?.data?.error ||
                            "something went wrong! try again"
                        );
                        if(isPtlFlow){
                            // not able to send from BE in case of password failed as error is returned by warden
                            ptlParentLoginFailedEvent({ isEmailUpdated: params?.masked_email !== email, loginMethod: otpLogin ? "Verification code" : "Password", errorMessage: err?.response?.data?.debug_message || err?.response?.data?.error, ...referralData })
                        }
                    });
                sendEvent(`Submit Login Details : ${primaryInputLabel}/Password`);
            } else {
                setError("Can't be blank");
            }
        }
    };

    const socialLoginEnabled = useMemo(() => {
        return showSocialLogin && !(window.navigator.userAgent.toLocaleLowerCase().match(/fban|fbav|fbios/)); //detects in app facebook browser by user agent
    }, [showSocialLogin, window.navigator.userAgent])

    const handleForgotPassword = () => {
        window.open(`/ptl/forgot_password${window.location.search}`, "_system");
    }

    return {
        handleLogin,
        otpSent,
        setOTPSent,
        setIntentId,
        sendEvent,
        email,
        setEmail,
        error,
        setError,
        otpLogin,
        password,
        setPassword,
        timer,
        sendOTP,
        otp,
        setOTP,
        showResendSuccessMsg,
        setOTPLogin,
        socialLoginEnabled,
        maskedEmail: params?.masked_email,
        params,        
        emailEvents,
        setEmailEvents,
        passwordEvents,
        setPasswordEvents,
        setFromPage,
        referralData,
        handleForgotPassword
    }
}